import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, Outlet, ScrollRestoration } from "react-router";
import { RouterProvider } from "react-router/dom";
import { ApiError } from "../../api/client";
import { CrashScreen } from "../../components/CrashScreen";
import { StoreShell } from "../../components/catalogue/StoreShell";
import { AccountRoute } from "../account/AccountRoute";
import { ConfirmEmailRoute } from "../account/ConfirmEmailRoute";
import { ForgotPasswordRoute } from "../account/ForgotPasswordRoute";
import { RegisterRoute } from "../account/RegisterRoute";
import { SignInLinkRoute } from "../account/SignInLinkRoute";
import { SignInRoute } from "../account/SignInRoute";
import { CartRoute } from "../cart/CartRoute";
import { CorporateRoute } from "../corporate/CorporateRoute";
import { InquiryRoute } from "../pages/InquiryRoute";
import { ContactRoute, DeliveryRoute, ReturnsRoute } from "../pages/StaticPages";
import { CheckoutRoute } from "../cart/CheckoutRoute";
import { OrderRoute } from "../orders/OrderRoute";
import { OrdersRoute } from "../orders/OrdersRoute";
import { AssemblyRoute } from "./AssemblyRoute";
import { EquipmentCategoriesRoute } from "./EquipmentCategoriesRoute";
import { EquipmentModelRoute } from "./EquipmentModelRoute";
import { ListingRoute } from "./ListingRoute";
import { NotFound } from "./NotFoundRoute";
import { ProductTypeRoute, ProductTypesRoute } from "./ProductTypesRoute";
import { SearchRoute } from "./SearchRoute";
import { StorefrontRoute } from "./StorefrontRoute";

// A 404 is an answer, not a blip: don't retry it.
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2 } },
});

function Layout() {
  return (
    <StoreShell>
      <ScrollRestoration />
      <Outlet />
    </StoreShell>
  );
}

// Catalogue paths mirror the API's (design doc §5.3), hyphenated like the admin's. The
// account screens use plain words instead: /sign-in reads better than /session.
const crashed = <CrashScreen home="/" homeLabel="Go to the catalogue" />;

const router = createBrowserRouter([
  {
    element: <Layout />,
    errorElement: crashed,
    children: [
      { index: true, element: <StorefrontRoute /> },
      { path: "equipment-categories", element: <EquipmentCategoriesRoute /> },
      { path: "equipment-models/:id", element: <EquipmentModelRoute /> },
      { path: "assemblies/:id", element: <AssemblyRoute /> },
      { path: "listings/:id", element: <ListingRoute /> },
      { path: "product-types", element: <ProductTypesRoute /> },
      { path: "product-types/:id", element: <ProductTypeRoute /> },
      { path: "search", element: <SearchRoute /> },
      { path: "cart", element: <CartRoute /> },
      { path: "checkout", element: <CheckoutRoute /> },
      { path: "orders", element: <OrdersRoute /> },
      { path: "orders/:reference", element: <OrderRoute /> },
      { path: "account", element: <AccountRoute /> },
      { path: "corporate", element: <CorporateRoute /> },
      { path: "ask", element: <InquiryRoute /> },
      { path: "contact", element: <ContactRoute /> },
      { path: "delivery", element: <DeliveryRoute /> },
      { path: "returns", element: <ReturnsRoute /> },
      { path: "*", element: <NotFound /> },
    ],
  },
  // The sign-in screens own the viewport: no storefront header (design system CustomerLogin).
  {
    errorElement: crashed,
    element: (
      <>
        <ScrollRestoration />
        <Outlet />
      </>
    ),
    children: [
      { path: "sign-in", element: <SignInRoute /> },
      { path: "sign-in/link", element: <SignInLinkRoute /> },
      { path: "register", element: <RegisterRoute /> },
      { path: "forgot-password", element: <ForgotPasswordRoute /> },
      { path: "confirm-email", element: <ConfirmEmailRoute /> },
    ],
  },
]);

export function CustomerApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
